// src/utils/inputHandlers.js

/**
 * Handles changes to a text input field and clears the corresponding error.
 * @param {Function} setValue - Function to update the state value.
 * @param {string} fieldName - The name of the field in the formErrors state.
 * @param {Object} formErrors - The current form errors state.
 * @param {Function} setFormErrors - Function to update the form errors state.
 */
export const handleTextChange =
  (setValue, fieldName, formErrors, setFormErrors) => (e) => {
    setValue(e.target.value);
    if (formErrors[fieldName]) {
      setFormErrors((prevErrors) => ({ ...prevErrors, [fieldName]: null }));
    }
  };

/**
 * Handles changes to a number input field, allowing only numeric values,
 * enforcing a min/max range, and clearing the corresponding error.
 * @param {Function} setValue - Function to update the state value.
 * @param {string} fieldName - The name of the field to clear errors for.
 * @param {Object} formErrors - The current form errors state.
 * @param {Function} setFormErrors - Function to update the form errors state.
 * @param {number} max - The maximum allowed value.
 * @param {number} min - The minimum allowed value.
 */
export const handleNumberChange =
  (setValue, fieldName, formErrors, setFormErrors, max = 1250000, min = 0) =>
  (e) => {
    const inputValue = e.target.value;

    // Allow only numbers
    if (!/^\d*$/.test(inputValue)) {
      return; // Ignore non-numeric input
    }

    let numericValue = Number(inputValue);

    // Determine effective min and max based on fieldName
    const effectiveMin = fieldName === "digitalSupply" ? 1 : min;
    const effectiveMax = fieldName === "digitalSupply" ? 2000 : max;

    // Enforce minimum
    if (numericValue < effectiveMin) {
      numericValue = effectiveMin;
    }

    // For fields other than digitalSupply, round to nearest multiple of 5
    if (fieldName !== "digitalSupply") {
      numericValue = Math.round(numericValue / 5) * 5;
    }

    // Enforce maximum
    if (numericValue > effectiveMax) {
      numericValue = effectiveMax;
    }

    // Update state
    setValue(numericValue);

    // Clear the corresponding error if present
    if (formErrors[fieldName]) {
      setFormErrors((prevErrors) => ({ ...prevErrors, [fieldName]: null }));
    }
  };
